/**
 * complete-your-set.js
 * Handles:
 *   1. "+ Add" → Shopify Ajax /cart/add.js → refresh cart drawer
 *   2. Variant <select> change → update card price / image / button state
 *   3. Scroll dot indicators sync
 *   4. Main product variant change → re-match accessory color variant
 *
 * No external dependencies. Shopify OS 2.0 compatible.
 * Place in: assets/complete-your-set.js
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     CART
  ───────────────────────────────────────────────────────────── */

  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (err) { throw err; });
      return res.json();
    });
  }

  function refreshCartDrawer() {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true, cancelable: false }));
  }

  /* ─────────────────────────────────────────────────────────────
     MONEY FORMAT
  ───────────────────────────────────────────────────────────── */

  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat)
      || (window.Shopify && window.Shopify.money_format)
      || '{{amount}}';
    var amount = (cents / 100).toFixed(2);
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  /* ─────────────────────────────────────────────────────────────
     UPDATE CARD UI
     Called whenever a variant changes (select or main-product color match).
  ───────────────────────────────────────────────────────────── */

  function updateCard(card, variant) {
    if (!variant) return;

    // Update stored variant id on card + button
    card.dataset.variantId = variant.id;
    var btn = card.querySelector('.js-complete-set-add');
    if (btn) btn.dataset.variantId = variant.id;

    // Availability
    var available = variant.available;
    if (btn) {
      btn.disabled = !available;
      btn.setAttribute('aria-disabled', !available);
      btn.textContent = available ? '+ Add' : 'Sold out';
      btn.classList.toggle('complete-set__add-btn--soldout', !available);
    }

    // Prices
    // Custom prices override: read from card data attributes (set by Liquid)
    var customPrice   = parseInt(card.dataset.customPrice, 10) || 0;
    var customCompare = parseInt(card.dataset.customCompare, 10) || 0;

    var displayPrice   = customPrice   > 0 ? customPrice   : variant.price;
    var displayCompare = customCompare > 0 ? customCompare : (variant.compare_at_price || 0);

    var priceEl   = card.querySelector('.js-cs-price');
    var compareEl = card.querySelector('.js-cs-compare');

    if (priceEl) {
      priceEl.textContent = formatMoney(displayPrice);
      priceEl.classList.toggle('complete-set__price--sale', displayCompare > displayPrice);
    }
    if (compareEl) {
      if (displayCompare > displayPrice) {
        compareEl.textContent = formatMoney(displayCompare);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Image — only update if variant carries its own image
    if (variant.featured_image) {
      var imgEl = card.querySelector('.complete-set__img');
      if (imgEl) {
        var src = variant.featured_image.src || '';
        if (src) {
          // Shopify image URL: add _300x300 size suffix before extension
          src = src.replace(/(\.\w{3,4})(\?.*)?$/, '_300x300$1');
          imgEl.src = src;
          imgEl.alt = variant.featured_image.alt || '';
        }
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────
     VARIANT SELECT CHANGE
  ───────────────────────────────────────────────────────────── */

  function handleSelectChange(event) {
    var select = event.target;
    var card   = select.closest('.complete-set__card');
    if (!card) return;

    var selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    // Read variant data from the <option> data attributes (set by Liquid)
    var variantId = parseInt(select.value, 10);
    var price     = parseInt(selectedOption.dataset.price, 10) || 0;
    var compare   = parseInt(selectedOption.dataset.compare, 10) || 0;
    var available = selectedOption.dataset.available !== 'false';
    var imgSrc    = selectedOption.dataset.imgSrc || '';
    var imgAlt    = selectedOption.dataset.imgAlt || '';

    // Build a lightweight variant-like object so updateCard can handle it
    var variantLike = {
      id: variantId,
      price: price,
      compare_at_price: compare,
      available: available,
      featured_image: imgSrc ? { src: imgSrc, alt: imgAlt } : null,
    };

    updateCard(card, variantLike);
  }

  /* ─────────────────────────────────────────────────────────────
     ADD TO CART CLICK
  ───────────────────────────────────────────────────────────── */

  function handleAddClick(event) {
    var btn = event.currentTarget;
    if (btn.disabled || btn.classList.contains('is-loading')) return;

    var variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) {
      console.warn('[complete-your-set] Missing data-variant-id on button', btn);
      return;
    }

    var originalText = btn.textContent;
    btn.classList.add('is-loading');
    btn.textContent = '…';

    addToCart(variantId, 1)
      .then(function () {
        btn.textContent = '✓ Added';
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        refreshCartDrawer();

        setTimeout(function () {
          btn.textContent = originalText;
          btn.classList.remove('is-added');
        }, 2200);
      })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        btn.textContent = originalText;
        var msg = (err && err.description) ? err.description : 'Could not add item.';
        alert(msg);
        console.error('[complete-your-set] Add to cart error:', err);
      });
  }

  /* ─────────────────────────────────────────────────────────────
     MAIN PRODUCT VARIANT CHANGE → RE-MATCH ACCESSORY COLOR
  ───────────────────────────────────────────────────────────── */

  function onMainVariantChange(event) {
    var variant = event.detail && event.detail.variant ? event.detail.variant : null;
    if (!variant) return;

    var mainColor = '';
    var colorOptionNames = ['color', 'colour', 'color/pattern', 'style'];
    var productJsonEl = document.getElementById('complete-set-product-json');
    if (productJsonEl) {
      try {
        var productData = JSON.parse(productJsonEl.textContent);
        (productData.options || []).forEach(function (opt, idx) {
          if (colorOptionNames.indexOf(opt.toLowerCase()) !== -1) {
            mainColor = ((variant.options && variant.options[idx]) || '').toLowerCase();
          }
        });
      } catch (e) { /* ignore */ }
    }

    if (!mainColor) return;

    document.querySelectorAll('.complete-set__card').forEach(function (card) {
      var variantsEl = card.querySelector('.js-complete-set-variants-json');
      if (!variantsEl) return;

      var variants;
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { return; }

      // Find matching available variant
      var matched = null;
      variants.forEach(function (v) {
        if (matched) return;
        if (!v.available) return;
        var allOpts = (v.options || []).map(function (o) { return (o || '').toLowerCase(); });
        if (allOpts.some(function (o) { return o.indexOf(mainColor) !== -1 || mainColor.indexOf(o) !== -1; })) {
          matched = v;
        }
      });

      if (!matched) matched = variants.find(function (v) { return v.available; }) || variants[0];
      if (!matched) return;

      // Sync the <select> if it exists
      var select = card.querySelector('.js-cs-variant-select');
      if (select) {
        for (var i = 0; i < select.options.length; i++) {
          if (parseInt(select.options[i].value, 10) === matched.id) {
            select.selectedIndex = i;
            break;
          }
        }
      }

      updateCard(card, matched);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     SCROLL DOT INDICATORS
  ───────────────────────────────────────────────────────────── */

  function initDots() {
    document.querySelectorAll('.complete-set__grid[id]').forEach(function (grid) {
      var suffix = grid.id.replace('complete-set-grid-', '');
      var dotsContainer = document.getElementById('complete-set-dots-' + suffix);
      if (!dotsContainer) return;

      var dots  = dotsContainer.querySelectorAll('.complete-set__dot');
      var cards = grid.querySelectorAll('.complete-set__card');
      if (!dots.length || !cards.length) return;

      // Dot click → smooth scroll to card
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var idx = parseInt(dot.dataset.index, 10);
          var target = cards[idx];
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        });
      });

      // IntersectionObserver: highlight dot matching most-visible card
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio >= 0.5) {
            var idx = Array.prototype.indexOf.call(cards, entry.target);
            if (idx === -1) return;
            dots.forEach(function (d) { d.classList.remove('is-active'); });
            if (dots[idx]) dots[idx].classList.add('is-active');
          }
        });
      }, { root: grid, threshold: 0.5 });

      cards.forEach(function (card) { observer.observe(card); });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     INIT — bind all events
  ───────────────────────────────────────────────────────────── */

  function init() {
    // "+ Add" buttons
    document.querySelectorAll('.js-complete-set-add').forEach(function (btn) {
      if (btn.dataset.csInitialized) return;
      btn.dataset.csInitialized = '1';
      btn.addEventListener('click', handleAddClick);
    });

    // Variant selects
    document.querySelectorAll('.js-cs-variant-select').forEach(function (select) {
      if (select.dataset.csInitialized) return;
      select.dataset.csInitialized = '1';
      select.addEventListener('change', handleSelectChange);
    });

    // Main product variant change (theme fires this on the form or document)
    document.querySelectorAll('.js-product-form, product-form, [data-product-id]').forEach(function (form) {
      form.addEventListener('on:variant:change', onMainVariantChange);
    });
    document.addEventListener('on:variant:change', onMainVariantChange);

    // Scroll dot indicators
    initDots();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CompleteYourSet = { init: init, initDots: initDots };

})();
